import {
  CategoriesFailure,
  CategoriesRepository,
  CategoryType,
  Either,
  GeneralCategory,
  left,
  right,
  SubCategory,
} from "../../domain";
import { logger } from "../../utils";
import { CategoriesRemoteDataSourceError } from "../core";
import { CategoriesRemoteDataSource } from "../data-sources";

export class CategoriesRepositoryImpl implements CategoriesRepository {
  constructor(private readonly remoteDataSource: CategoriesRemoteDataSource) {}

  private handleError(
    error: CategoriesRemoteDataSourceError
  ): CategoriesFailure {
    if (error === "NetworkError") return CategoriesFailure.networkFailure();
    return CategoriesFailure.serverFailure();
  }

  public async addGeneralCategory(
    title: string
  ): Promise<Either<CategoriesFailure, null>> {
    try {
      await this.remoteDataSource.addGeneralCategory(title);
      return right(null);
    } catch (error) {
      return left(this.handleError(error));
    }
  }

  public async deleteGeneralCategory(
    generalCategory: GeneralCategory
  ): Promise<Either<CategoriesFailure, null>> {
    try {
      await this.remoteDataSource.deleteGeneralCategory(generalCategory.id);
      await Promise.all(
        generalCategory.subCategories.map(async (subCategory) => {
          await this.remoteDataSource.deleteSubCategory(subCategory.id);
          await Promise.all(
            subCategory.categories.map(
              async (category) =>
                await this.remoteDataSource.deleteCategory(category.id)
            )
          );
        })
      );
      return right(null);
    } catch (error) {
      return left(this.handleError(error));
    }
  }

  public async listCategories(): Promise<
    Either<CategoriesFailure, GeneralCategory[]>
  > {
    try {
      const res = await this.remoteDataSource.listGeneralCategories();
      return right(res);
    } catch (error) {
      return left(this.handleError(error));
    }
  }

  public async addSubCategory(
    title: string,
    generalCategoryId: string
  ): Promise<Either<CategoriesFailure, null>> {
    try {
      const subCategory = await this.remoteDataSource.addSubCategory(title);
      console.log(subCategory);

      await this.remoteDataSource.addSubCategoryToGeneralCatgory(
        subCategory.id,
        generalCategoryId
      );
      return right(null);
    } catch (error) {
      logger.error(error);
      return left(this.handleError(error));
    }
  }

  public async deleteSubCategory(
    subCategory: SubCategory
  ): Promise<Either<CategoriesFailure, null>> {
    try {
      await this.remoteDataSource.deleteSubCategory(subCategory.id);
      await Promise.all(
        subCategory.categories.map(
          async (category) =>
            await this.remoteDataSource.deleteCategory(category.id)
        )
      );
      return right(null);
    } catch (error) {
      return left(this.handleError(error));
    }
  }

  public async addCategory(
    title: string,
    subCategoryId: string
  ): Promise<Either<CategoriesFailure, null>> {
    try {
      const category = await this.remoteDataSource.addCategory(title);
      await this.remoteDataSource.addCategoryToSubCatgory(
        category.id,
        subCategoryId
      );
      return right(null);
    } catch (error) {
      return left(this.handleError(error));
    }
  }

  public async deleteCategory(
    id: string
  ): Promise<Either<CategoriesFailure, null>> {
    try {
      await this.remoteDataSource.deleteCategory(id);
      return right(null);
    } catch (error) {
      return left(this.handleError(error));
    }
  }

  public async editCategory(
    categoryType: CategoryType,
    id: string,
    title: string
  ): Promise<Either<CategoriesFailure, null>> {
    try {
      await this.remoteDataSource.editCategory(categoryType, id, title);
      return right(null);
    } catch (error) {
      return left(this.handleError(error));
    }
  }
}
